// Editor κάτοψης του ταμείου: σύρσιμο τραπεζιών, περιοχών, ανοιγμάτων και σταθερών
// στοιχείων· ρυθμίσεις στα δεξιά· Αναίρεση/Επανάληψη· έλεγχοι πριν την αποθήκευση.
//
// Ίδιες επιλογές με τον editor της Sala και ΙΔΙΟ μοντέλο: ο κώδικας γεωμετρίας είναι κοινός
// (utils/salaFloor, αντιγράφεται από τη Sala) και η αποθήκευση πάει στο ίδιο /api/floor, που
// ελέγχει όλη την κάτοψη. Ό,τι αλλάξει εδώ το βλέπει αμέσως και η Sala, και αντίστροφα.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, GripVertical, Magnet, Phone, Plus, Redo2, Undo2 } from 'lucide-react';
import { useToast } from '../../shared/Toast';
import { useAuth } from '../../../hooks/useAuth';
import { registerDirtySource } from '../../../utils/unsavedChanges';
import { boundsOf, floorIssues, fromApi, roomAt, toApi, type FloorApi, type FloorDoc } from '../../../utils/salaFloor/floor';
import { addRoom, applyDrag, link, type Drag, type Guide } from '../../../utils/salaFloor/floorEdit';
import { FloorError, getFloor, saveFloor } from './api';
import { useFloorHistory } from './history';
import Inspector, { useIssueText, type Mode, type Sel } from './Inspector';
import FloorSetup from './FloorSetup';
import { C, FixtureLabel, RoomShape, TableFace, VenueViewport, fixtureStyle } from './Venue';
import { Btn } from './ui';

const HANDLES: [string, number, number, string][] = [
  ['nw', 0, 0, 'cursor-nwse-resize'], ['n', 0.5, 0, 'cursor-ns-resize'], ['ne', 1, 0, 'cursor-nesw-resize'], ['e', 1, 0.5, 'cursor-ew-resize'],
  ['se', 1, 1, 'cursor-nwse-resize'], ['s', 0.5, 1, 'cursor-ns-resize'], ['sw', 0, 1, 'cursor-nesw-resize'], ['w', 0, 0.5, 'cursor-ew-resize'],
];

/** Το ύψος ως το κάτω μέρος της οθόνης (όχι 100vh: το ταμείο βάζει zoom στη ρίζα). */
function useFillHeight(min = 520) {
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(min);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const zoom = el.offsetWidth > 0 && rect.width > 0 ? rect.width / el.offsetWidth : 1;
      setH(Math.max(min, (window.innerHeight - rect.top - 16) / zoom));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [min]);
  return { ref, h };
}

export default function FloorEditorScreen() {
  const { t } = useTranslation(['floorEditor']);
  const { store } = useAuth();
  const [api, setApi] = useState<FloorApi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => {
    if (!store?.id) return;
    setError(null);
    getFloor(store.id).then(setApi).catch(e => setError(e instanceof Error ? e.message : String(e)));
  };
  useEffect(load, [store?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const fill = useFillHeight();

  return (
    <div ref={fill.ref} style={{ height: fill.h }} className="overflow-hidden rounded-xl border border-line bg-surface-0">
      {error ? (
        <div className="grid h-full place-items-center p-6 text-center">
          <div className="grid justify-items-center gap-3">
            <p className="text-t2">{t('floorEditor:editor.loadFailed', { error })}</p>
            <Btn onClick={load}>{t('floorEditor:editor.retry')}</Btn>
          </div>
        </div>
      ) : !api || !store?.id ? (
        <div className="grid h-full place-items-center text-t3">{t('floorEditor:editor.loading')}</div>
      ) : (
        <FloorEditor api={api} storeId={store.id} storeName={store.store_name || store.name || ''} onSaved={setApi} />
      )}
    </div>
  );
}

function FloorEditor({ api, storeId, storeName, onSaved }: { api: FloorApi; storeId: number; storeName: string; onSaved: (a: FloorApi) => void }) {
  const { t } = useTranslation(['floorEditor']);
  const k = (key: string, vars?: Record<string, unknown>) => String(t(`floorEditor:${key}`, vars as never));
  const { showToast } = useToast();
  const toast = (type: 'success' | 'error' | 'warning' | 'info', text: string) => showToast(type, text);
  const issueText = useIssueText();
  const initial = useMemo(() => fromApi(api), []); // eslint-disable-line react-hooks/exhaustive-deps
  const h = useFloorHistory(initial);
  const [setup, setSetup] = useState(api.zones.length === 0);
  const [saving, setSaving] = useState(false);
  const [tmp, setTmp] = useState<FloorDoc | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  const [sel, setSel] = useState<Sel>(null);
  const [mode, setMode] = useState<Mode>(null);
  const [area, setArea] = useState<string>('all');
  const [snap, setSnap] = useState(true);
  const scaleRef = useRef(1);
  const drag = useRef<{ d: Drag; sx: number; sy: number; base: FloorDoc; moved: boolean; last: FloorDoc | null } | null>(null);

  const doc = tmp || h.doc;
  const issues = useMemo(() => floorIssues(h.doc), [h.doc]);
  const errors = issues.filter(i => i.level === 'error');
  const bad = useMemo(() => {
    const m = new Map<string, 'error' | 'warn'>();
    issues.forEach(i => i.keys.forEach(key => m.set(key, i.level === 'error' || m.get(key) === 'error' ? 'error' : 'warn')));
    return m;
  }, [issues]);
  const dirty = h.changes > 0;
  const prefix = k('defaults.tablePrefix');

  // Χωρίς αποθήκευση: το κοινό μητρώο της διαχείρισης ρωτά πριν αλλάξει καρτέλα (Σάλα/μενού) και ο
  // browser πριν κλείσει/ανανεωθεί τη σελίδα.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  useEffect(() => registerDirtySource('floor-editor', () => dirtyRef.current), []);

  const change = (next: FloorDoc, key?: string | null) => h.set(next, key);
  const select = (s: Sel) => { setSel(s); setMode(null); };
  const shownRoom = (key: string) => area === 'all' || area === key;
  const viewRooms = area === 'all' ? h.doc.rooms : h.doc.rooms.filter(r => r.key === area);
  const bounds = useMemo(() => boundsOf(viewRooms.length ? viewRooms : h.doc.rooms), [viewRooms, h.doc.rooms]);
  const linkFrom = mode === 'link' && sel?.k === 't' ? sel.key : null;

  const ptr = (d: Drag, s: Sel) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      if (linkFrom && d.kind === 't') {
        const out = link(h.doc, linkFrom, d.key);
        if (out.added) {
          change(out.doc);
          const a = h.doc.tables.find(x => x.key === linkFrom);
          const b = h.doc.tables.find(x => x.key === d.key);
          toast('success', k('editor.linked', { a: a?.n, b: b?.n, seats: (a?.cap || 0) + (b?.cap || 0) }));
        } else if (linkFrom !== d.key) toast('info', k('editor.alreadyLinked'));
        setMode(null);
        return;
      }
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = { d, sx: e.clientX, sy: e.clientY, base: h.doc, moved: false, last: null };
      select(s);
    },
    onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
      const g = drag.current;
      if (!g) return;
      if (!g.moved && Math.abs(e.clientX - g.sx) < 4 && Math.abs(e.clientY - g.sy) < 4) return;
      g.moved = true;
      const s = scaleRef.current || 1;
      const out = applyDrag(g.base, g.d, (e.clientX - g.sx) / s, (e.clientY - g.sy) / s, snap && !e.altKey);
      g.last = out.doc;
      setTmp(out.doc);
      setGuides(out.guides);
    },
    onPointerUp: () => {
      const g = drag.current;
      drag.current = null;
      setGuides([]);
      setTmp(null);
      if (g?.moved && g.last) change(g.last);
    },
    onPointerCancel: () => { drag.current = null; setTmp(null); setGuides([]); },
  });

  const nudge = (key: string) => (e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 1 : 10;
    const dir: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    const v = dir[e.key];
    if (!v) return;
    e.preventDefault();
    change({ ...h.doc, tables: h.doc.tables.map(x => (x.key === key ? { ...x, x: x.x + v[0], y: x.y + v[1] } : x)) }, `nudge:${key}`);
  };

  const doSave = async () => {
    if (errors.length) {
      const first = errors[0];
      const tb = h.doc.tables.find(x => x.key === first.keys[0]);
      select(tb ? { k: 't', key: tb.key } : null);
      toast('error', k('editor.fixFirst', { text: issueText(first) }));
      return;
    }
    setSaving(true);
    try {
      const fresh = await saveFloor(storeId, toApi(h.doc, api.store.reservations));
      onSaved(fresh);
      h.reset(fromApi(fresh));
      setSel(null);
      setMode(null);
      toast('success', k('editor.savedToast'));
    } catch (e) {
      const code = e instanceof FloorError ? e.body.code : null;
      if (code === 'table_in_use') toast('error', k('editor.tableInUse', { tables: String((e as FloorError).body.tables) }));
      else if (e instanceof FloorError && e.status === 403) toast('error', k('editor.noPermission'));
      else toast('error', k('editor.saveFailed', { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setSaving(false);
    }
  };

  if (setup) {
    return <FloorSetup storeName={storeName} onDone={d => { h.reset(d, true); setSetup(false); }} />;
  }

  const selRoom = sel && (sel.k === 'room' || sel.k === 'op') ? sel.room : null;
  const selTable = sel?.k === 't' ? sel.key : null;
  const names = { bar: k('add.bar'), column: k('add.column'), plant: k('add.plant'), label: k('add.label') };
  const types = { entrance: k('add.entrance'), door: k('add.door'), window: k('add.window') };

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-1 px-3 py-2.5">
        <div className="flex min-w-0 items-baseline gap-2 pl-1">
          <h2 className="text-[19px] font-bold text-t1">{k('editor.title')}</h2>
          <span className="truncate text-[13px] text-t3">{storeName}</span>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <Btn variant="ghost" size="icon" aria-label={k('editor.undo')} title={k('editor.undo')} onClick={h.undo} disabled={!h.canUndo}><Undo2 className="h-5 w-5" /></Btn>
          <Btn variant="ghost" size="icon" aria-label={k('editor.redo')} title={k('editor.redo')} onClick={h.redo} disabled={!h.canRedo}><Redo2 className="h-5 w-5" /></Btn>
          <span className={`hidden min-w-[150px] text-right text-[13px] font-semibold md:inline ${errors.length ? 'text-red-600 dark:text-red-400' : dirty ? 'text-amber-600 dark:text-amber-400' : 'text-t3'}`}>
            {errors.length ? k('editor.errors', { count: errors.length }) : dirty ? k('editor.changes', { count: h.changes }) : k('editor.saved')}
          </span>
          <Btn onClick={() => { h.reset(fromApi(api)); setSel(null); setMode(null); toast('info', k('editor.discarded')); }} disabled={!dirty}>{k('editor.discard')}</Btn>
          <Btn variant="primary" icon={<CheckCircle className="h-4 w-4" />} onClick={doSave} disabled={(!dirty && !errors.length) || saving}>
            {saving ? k('editor.saving') : k('editor.save')}
          </Btn>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-1.5 border-b border-line bg-surface-1 px-4 py-2">
        {[{ key: 'all', name: k('editor.allSpace') }, ...h.doc.rooms.map(r => ({ key: r.key, name: r.name.trim() || k('common.noName') }))].map(a => (
          <button key={a.key} type="button" aria-pressed={area === a.key} onClick={() => setArea(a.key)}
            className={`h-9 whitespace-nowrap rounded-full border px-3.5 text-[13.5px] font-semibold transition-colors ${area === a.key ? 'border-blue-600 bg-blue-600 text-white' : 'border-line bg-surface-1 text-t2 hover:border-line-strong'}`}>
            {a.name}
          </button>
        ))}
        <button type="button" onClick={() => { const out = addRoom(h.doc, k('defaults.newRoom')); change(out.doc); select({ k: 'room', room: out.key }); setArea('all'); toast('info', k('editor.roomAdded')); }}
          className="inline-flex h-9 items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-line-strong px-3 text-[13.5px] font-semibold text-t2 hover:text-t1">
          <Plus className="h-4 w-4" /> {k('editor.addRoom')}
        </button>
        <Btn className="ml-auto" variant={mode === 'add' ? 'primary' : 'secondary'} icon={<Plus className="h-4 w-4" />} aria-pressed={mode === 'add'}
          onClick={() => setMode(mode === 'add' ? null : 'add')}>{k('editor.add')}</Btn>
      </div>

      <div className="flex min-h-0 flex-1">
        <div className="flex min-w-0 flex-1 flex-col">
          <VenueViewport bounds={bounds} scaleRef={scaleRef} className="min-h-0 flex-1 select-none bg-surface-0" pad={[24, 44, 24, 24]}
            underlay={<button type="button" aria-label={k('editor.deselect')} tabIndex={-1} className="absolute inset-0 cursor-default" onClick={() => select(null)} />}
            overlay={banner()}>
            {doc.rooms.map(r => {
              const on = shownRoom(r.key);
              const selected = sel?.k === 'room' && sel.room === r.key;
              const n = doc.tables.filter(x => x.x >= r.x && x.x <= r.x + r.w && x.y >= r.y && x.y <= r.y + r.h);
              return (
                <div key={r.key} className={`transition-opacity duration-300 ${on ? '' : 'pointer-events-none opacity-0'}`}>
                  <RoomShape room={r} grid outline={selected ? `2px dashed ${C.focus}` : bad.has(r.key) ? `2px dashed ${C.error}` : undefined} />
                  <button type="button" aria-label={k('editor.roomLabel', { name: r.name })} onClick={() => select({ k: 'room', room: r.key })}
                    className="absolute rounded-md" style={{ left: r.x, top: r.y, width: r.w, height: r.h }} />
                  <button type="button" aria-label={k('editor.moveRoom', { name: r.name })} {...ptr({ kind: 'rm', key: r.key }, { k: 'room', room: r.key })}
                    className={`absolute flex h-7 touch-none cursor-grab items-center gap-1.5 whitespace-nowrap rounded-full border pl-1 pr-2.5 shadow-sm active:cursor-grabbing ${selected ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40' : 'border-line bg-surface-1'}`}
                    style={{ left: r.x, top: r.y - 34 }}>
                    <GripVertical className="h-4 w-4 text-t3" />
                    <span className="text-[14px] font-bold text-t1">{r.name.trim() || k('common.noName')}</span>
                    <span className="text-[12px] text-t3">{k('common.tables', { count: n.length })} · {k('common.seats', { count: n.reduce((s, x) => s + x.cap, 0) })}</span>
                  </button>
                  {r.openings.map(o => {
                    const horiz = o.side === 'top' || o.side === 'bottom';
                    const edge = o.side === 'top' ? r.y : o.side === 'bottom' ? r.y + r.h : o.side === 'left' ? r.x : r.x + r.w;
                    const opSel = sel?.k === 'op' && sel.id === o.id;
                    return (
                      <button key={o.id} type="button" aria-label={`${types[o.type]}${o.label ? ` ${o.label}` : ''}`}
                        {...ptr({ kind: 'op', key: o.id, room: r.key }, { k: 'op', room: r.key, id: o.id })}
                        className={`absolute touch-none rounded-md ${horiz ? 'cursor-ew-resize' : 'cursor-ns-resize'} ${opSel ? 'bg-blue-500/20 outline-dashed outline-2 outline-offset-2 outline-blue-500' : ''}`}
                        style={horiz ? { left: r.x + o.at, top: edge - 13, width: o.len, height: 26 } : { left: edge - 13, top: r.y + o.at, width: 26, height: o.len }} />
                    );
                  })}
                </div>
              );
            })}

            {doc.combos.map(c => {
              const ts = c.tables.map(key => doc.tables.find(x => x.key === key)).filter(Boolean) as FloorDoc['tables'];
              const hot = !!selTable && c.tables.includes(selTable);
              return ts.slice(1).map((b, i) => {
                const a = ts[i];
                const len = Math.hypot(b.x - a.x, b.y - a.y);
                return (
                  <span key={`${c.key}${i}`} aria-hidden className="pointer-events-none absolute h-0 origin-top-left"
                    style={{ left: a.x, top: a.y, width: len, transform: `rotate(${Math.atan2(b.y - a.y, b.x - a.x)}rad)`, borderTop: hot ? `3px dashed ${C.focus}` : `2px dotted ${C.lineStrong}` }} />
                );
              });
            })}

            {doc.fixtures.map(f => {
              const r = roomAt(doc.rooms, f.x + f.w / 2, f.y + f.h / 2);
              const on = !r || shownRoom(r.key);
              const s = fixtureStyle(f);
              const selected = sel?.k === 'fx' && sel.id === f.id;
              return (
                <button key={f.id} type="button" aria-label={`${names[f.type]}${f.label ? ` ${f.label}` : ''}`} {...ptr({ kind: 'fx', key: f.id }, { k: 'fx', id: f.id })}
                  className={`${s.className} touch-none cursor-grab transition-opacity duration-300 active:cursor-grabbing ${on ? '' : 'pointer-events-none opacity-0'} ${selected ? 'outline-dashed outline-2 outline-offset-4 outline-blue-500' : ''}`}
                  style={s.style}>
                  <FixtureLabel f={f} />
                </button>
              );
            })}

            {doc.tables.map(tb => {
              const r = roomAt(doc.rooms, tb.x, tb.y);
              const on = !r || shownRoom(r.key);
              const selected = selTable === tb.key;
              const candidate = !!linkFrom && linkFrom !== tb.key;
              const issue = bad.get(tb.key);
              return (
                <button key={tb.key} type="button" onKeyDown={nudge(tb.key)}
                  aria-label={k('editor.tableAria', { n: tb.n, seats: k('common.seats', { count: tb.cap }) })}
                  {...ptr({ kind: 't', key: tb.key }, { k: 't', key: tb.key })}
                  className={`absolute flex touch-none cursor-grab flex-col items-center justify-center gap-[3px] border-2 bg-surface-1 text-t1 shadow-sm transition-opacity duration-300 active:cursor-grabbing ${tb.shape === 'round' ? 'rounded-full' : 'rounded-[16px]'} ${
                    issue === 'error' ? 'border-red-500' : issue === 'warn' ? 'border-amber-500' : 'border-line-strong'} ${
                    selected ? 'outline outline-[3px] outline-offset-4 outline-blue-500' : candidate ? 'outline-dashed outline-[3px] outline-offset-4 outline-blue-500' : ''} ${on ? '' : 'pointer-events-none opacity-0'}`}
                  style={{
                    left: tb.x - tb.w / 2, top: tb.y - tb.h / 2, width: tb.w, height: tb.h,
                    backgroundImage: tb.res ? undefined : `repeating-linear-gradient(135deg, hsl(var(--surface-3)) 0 6px, transparent 6px 12px)`,
                  }}>
                  <TableFace n={tb.n || '?'} w={tb.w} h={tb.h}
                    sub={<span className={`font-semibold ${tb.res ? 'text-t3' : 'text-t2'}`}>{tb.res ? k('common.seats', { count: tb.cap }) : k('editor.walkIn')}</span>} />
                  {(issue || (tb.res && !tb.online && api.store.reservations)) && (
                    <span className={`absolute -right-[9px] -top-[9px] grid h-[22px] w-[22px] place-items-center rounded-full border border-line bg-surface-1 shadow-sm ${issue === 'error' ? 'text-red-600' : issue ? 'text-amber-600' : 'text-t2'}`}>
                      {issue ? <AlertTriangle className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
                    </span>
                  )}
                </button>
              );
            })}

            {selRoom && (() => {
              const r = doc.rooms.find(x => x.key === selRoom);
              if (!r || sel?.k !== 'room') return null;
              return HANDLES.map(([hd, fx, fy, cur]) => (
                <button key={hd} type="button" aria-label={k('editor.resize', { h: hd })} {...ptr({ kind: 'rh', key: r.key, handle: hd }, { k: 'room', room: r.key })}
                  className={`absolute grid h-[30px] w-[30px] touch-none place-items-center ${cur}`}
                  style={{ left: r.x + r.w * fx - 15, top: r.y + r.h * fy - 15 }}>
                  <span className="h-[13px] w-[13px] rounded-[4px] border-2 border-blue-500 bg-surface-1" />
                </button>
              ));
            })()}

            {guides.map((g, i) => (
              <span key={i} aria-hidden className="pointer-events-none absolute"
                style={g.v ? { left: g.at, top: g.a, height: g.b - g.a, borderLeft: `1.5px dashed ${C.focus}` } : { left: g.a, top: g.at, width: g.b - g.a, borderTop: `1.5px dashed ${C.focus}` }} />
            ))}
          </VenueViewport>

          <div className="flex items-center gap-4 border-t border-line bg-surface-1 px-4 py-1.5 text-[12.5px] text-t3">
            <span className="whitespace-nowrap">
              {k('common.tables', { count: doc.tables.length })} · {k('common.seats', { count: doc.tables.reduce((s, x) => s + x.cap, 0) })} · {k('common.rooms', { count: doc.rooms.length })}
            </span>
            <span className="ml-auto hidden whitespace-nowrap xl:inline">{k('editor.footer')}</span>
            <button type="button" role="switch" aria-checked={snap} onClick={() => setSnap(!snap)}
              className="flex h-9 items-center gap-2 rounded-lg px-1 text-[12.5px] font-semibold text-t2">
              <Magnet className="h-4 w-4" /> {k('editor.magnet')}
              <span className={`relative block h-6 w-10 rounded-full transition-colors ${snap ? 'bg-blue-600' : 'bg-line-strong'}`}>
                <span className={`absolute left-0 top-[3px] h-[18px] w-[18px] rounded-full bg-white shadow transition-transform ${snap ? 'translate-x-[19px]' : 'translate-x-[3px]'}`} />
              </span>
            </button>
          </div>
        </div>

        <Inspector doc={h.doc} api={api} sel={sel} mode={mode} area={area} issues={issues}
          onChange={change} onSelect={select} onMode={setMode} onArea={setArea} toast={toast} />
      </div>
    </div>
  );

  function banner() {
    if (linkFrom) {
      const tb = h.doc.tables.find(x => x.key === linkFrom);
      return (
        <div className="absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full bg-t1 py-1.5 pl-4 pr-1.5 text-[13px] font-semibold text-surface-0 shadow-lg">
          {k('editor.linkBanner', { n: tb?.n, prefix })}
          <button type="button" onClick={() => setMode(null)} className="h-8 rounded-full bg-surface-0 px-3 text-[12.5px] font-bold text-t1">{k('common.cancel')}</button>
        </div>
      );
    }
    if (errors.length) {
      const first = errors[0];
      return (
        <div className="absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-3 whitespace-nowrap rounded-full bg-red-600 py-1.5 pl-4 pr-1.5 text-[13px] font-semibold text-white shadow-lg">
          {issueText(first)}
          <button type="button" className="h-8 rounded-full bg-white px-3 text-[12.5px] font-bold text-red-600"
            onClick={() => {
              const tb = h.doc.tables.find(x => x.key === first.keys[0]);
              const r = h.doc.rooms.find(x => x.key === first.keys[0]);
              select(tb ? { k: 't', key: tb.key } : r ? { k: 'room', room: r.key } : null);
            }}>{k('editor.see')}</button>
        </div>
      );
    }
    return null;
  }
}
